import {
  CaretRightOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  PauseOutlined
} from '@ant-design/icons';
import { Button, Empty, Input, Skeleton, Tooltip } from 'antd';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import tinycolor from 'tinycolor2';
import { Discovery, Family, familyName, Role, sortedFamilies } from './models/Discovery';
import { lineColor, otherColor } from './palette';

/**
 * The automatically discovered pattern families of the sheet (backend/api/analysis/discovery.py): the analysis that
 * the paper's users had to do by hand. Each family can be shown in the sheet, played and inspected.
 */

const Contour = ({
  contour,
  rhythm,
  color
}: {
  contour: number[];
  rhythm: number[];
  color: string;
}) => {
  const width = 72;
  const height = 24;
  if (contour.length < 2) return <svg aria-hidden height={height} width={width} />;
  const low = Math.min(...contour);
  const high = Math.max(...contour);
  const total = rhythm.reduce((sum, duration) => sum + duration, 0) || contour.length;
  let onset = 0;
  const points = contour.map((step, index) => {
    const point = `${2 + (onset / total) * (width - 4)},${height - 3 - ((step - low) / Math.max(high - low, 1)) * (height - 6)}`;
    onset += rhythm[index] ?? 1;
    return point;
  });

  return (
    <svg aria-hidden className="shrink-0" height={height} width={width}>
      <polyline
        fill="none"
        points={points.join(' ')}
        // Light highlighter colours (the theme's yellow) would vanish as a thin line on white.
        stroke={lineColor(color)}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
      />
    </svg>
  );
};

export const discoveryPanelID = 'discovery-panel';

const roleKey = (role: Role) => `discovery.roles.${role}`;

const DiscoveryPanel = ({
  colors,
  discovery,
  error,
  isLoading,
  labels,
  names,
  onPlay,
  onRename,
  onSelect,
  onToggle,
  onToggleAll,
  ownPatterns,
  playingLabel,
  selectedLabel,
  visibleLabels
}: {
  colors: Record<string, string>;
  discovery: Discovery | undefined;
  error: string | undefined;
  isLoading: boolean;
  labels: Record<string, string>;
  /** Names the analyst gave families (by discovery label); the others are named by their figure or role. */
  names: Record<string, string>;
  onPlay: (family: Family) => void;
  /** An empty name goes back to the starting name. */
  onRename: (family: Family, name: string) => void;
  onSelect: (family: Family) => void;
  onToggle: (family: Family) => void;
  onToggleAll: (visible: boolean) => void;
  /** Per family label, the labels of the user's own patterns among its entries. */
  ownPatterns: Record<string, string[]>;
  playingLabel: string | undefined;
  selectedLabel: string | undefined;
  visibleLabels: string[];
}) => {
  const { t: translate } = useTranslation();
  // The family whose name is being edited, and whether Escape cancelled the edit (the field then loses the focus).
  const [editingLabel, setEditingLabel] = useState<string>();
  const cancelled = useRef(false);

  if (isLoading) return <Skeleton active paragraph={{ rows: 6 }} title={false} />;
  if (error)
    return (
      <Empty
        description={
          <span className="text-slate-600">{translate('discovery.failed', { error })}</span>
        }
      />
    );
  if (!discovery || discovery.families.length === 0)
    return (
      <Empty description={<span className="text-slate-600">{translate('discovery.none')}</span>} />
    );

  const families = sortedFamilies(discovery.families);
  const share = Math.round(
    (100 * discovery.coverage.notes) / Math.max(discovery.coverage.total, 1)
  );
  const allVisible = families.every((family) => visibleLabels.includes(family.label));
  // Once the user has patterns of their own: the first families none of them touches, as a hint where to look next.
  const hasOwnPatterns = Object.values(ownPatterns).some((own) => own.length > 0);
  const notYetAnalysed = hasOwnPatterns
    ? families.filter((family) => (ownPatterns[family.label] ?? []).length === 0).slice(0, 4)
    : [];

  return (
    <section aria-label={translate('discovery.title')} className="flex min-h-0 flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <p className="m-0 text-xs text-slate-600" id="discovery-summary">
          {translate('discovery.summary', { count: families.length, share })}
        </p>
        <Button onClick={() => onToggleAll(!allVisible)} size="small">
          {allVisible ? translate('discovery.hideAll') : translate('discovery.showAll')}
        </Button>
      </div>
      {notYetAnalysed.length > 0 && (
        <p className="m-0 text-xs text-slate-600" id="discovery-next">
          {translate('discovery.next', {
            labels: notYetAnalysed.map((family) => labels[family.label] ?? family.label).join(', ')
          })}
        </p>
      )}
      <ul
        aria-describedby="discovery-summary"
        className="m-0 flex min-h-0 flex-col gap-1 overflow-y-auto p-0"
        style={{ listStyle: 'none' }}
      >
        {families.map((family) => {
          const isVisible = visibleLabels.includes(family.label);
          const isSelected = selectedLabel === family.label;
          const voices = Object.keys(family.distributions.byVoice).length;
          const color = colors[family.label] ?? otherColor;
          const name = familyName(family, labels, translate, names);
          const role = translate(roleKey(family.role));
          const finishEditing = (value: string) => {
            if (!cancelled.current) onRename(family, value);
            cancelled.current = false;
            setEditingLabel(undefined);
          };
          const badge = (
            <span
              className="flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full px-1 text-xs font-bold"
              style={{
                background: color,
                color: tinycolor.mostReadable(color, ['#ffffff', '#0b0b0b']).toHexString()
              }}
            >
              {labels[family.label]}
            </span>
          );

          return (
            <li key={family.label}>
              <div
                className={`flex items-center gap-2 rounded-md border border-solid px-2 py-1.5 ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 hover:border-slate-400'
                }`}
              >
                {/* Selecting is the card's main action; play and show are separate buttons beside it (no nesting). */}
                {editingLabel === family.label ? (
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    {badge}
                    <Input
                      aria-label={translate('discovery.renameField', {
                        label: labels[family.label]
                      })}
                      autoFocus
                      defaultValue={name}
                      onBlur={(event) => finishEditing(event.currentTarget.value)}
                      onFocus={(event) => event.currentTarget.select()}
                      onKeyDown={(event) => {
                        if (event.key === 'Escape') {
                          cancelled.current = true;
                          event.currentTarget.blur();
                        }
                      }}
                      onPressEnter={(event) => event.currentTarget.blur()}
                      placeholder={translate('discovery.renamePlaceholder')}
                      size="small"
                    />
                  </div>
                ) : (
                  <button
                    aria-pressed={isSelected}
                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left"
                    onClick={() => onSelect(family)}
                    style={{
                      background: 'transparent',
                      border: 0,
                      color: 'inherit',
                      font: 'inherit',
                      padding: 0
                    }}
                    type="button"
                  >
                    {badge}
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
                        {name}
                        {family.accompanies && (
                          <span className="font-normal text-slate-600">
                            {translate('discovery.accompanies', {
                              label: labels[family.accompanies] ?? family.accompanies
                            })}
                          </span>
                        )}
                        {(ownPatterns[family.label] ?? []).length > 0 && (
                          <span
                            className="rounded bg-slate-100 px-1.5 text-xs font-normal text-slate-700"
                            title={translate('discovery.yoursTooltip')}
                          >
                            {translate('discovery.yours', {
                              count: ownPatterns[family.label].length,
                              labels: ownPatterns[family.label].join(', ')
                            })}
                          </span>
                        )}
                      </span>
                      <span className="block text-xs text-slate-600">
                        {[
                          // The role, where the name does not say it.
                          ...(name === role ? [] : [role]),
                          translate('family.entries', { count: family.occurrences.length }),
                          translate('discovery.notes', { count: family.length }),
                          translate('family.voices', { count: voices })
                        ].join(' · ')}
                      </span>
                    </span>
                    <Contour color={color} contour={family.contour} rhythm={family.rhythm} />
                  </button>
                )}
                <Tooltip title={translate('discovery.rename')}>
                  <Button
                    aria-label={translate('discovery.renameField', { label: labels[family.label] })}
                    icon={<EditOutlined />}
                    onClick={(event) => {
                      event.stopPropagation();
                      cancelled.current = false;
                      setEditingLabel(family.label);
                    }}
                    shape="circle"
                    size="small"
                    type="text"
                  />
                </Tooltip>
                <Tooltip
                  title={translate(
                    playingLabel === family.label ? 'discovery.stop' : 'discovery.play'
                  )}
                >
                  <Button
                    aria-label={translate(
                      playingLabel === family.label ? 'discovery.stop' : 'discovery.play'
                    )}
                    icon={
                      playingLabel === family.label ? <PauseOutlined /> : <CaretRightOutlined />
                    }
                    onClick={(event) => {
                      event.stopPropagation();
                      onPlay(family);
                    }}
                    shape="circle"
                    size="small"
                  />
                </Tooltip>
                <Tooltip title={translate(isVisible ? 'discovery.hide' : 'discovery.show')}>
                  <Button
                    aria-label={translate(isVisible ? 'discovery.hide' : 'discovery.show')}
                    aria-pressed={isVisible}
                    icon={isVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                    onClick={(event) => {
                      event.stopPropagation();
                      onToggle(family);
                    }}
                    shape="circle"
                    size="small"
                    type={isVisible ? 'primary' : 'default'}
                  />
                </Tooltip>
              </div>
            </li>
          );
        })}
      </ul>
      <p className="m-0 text-xs text-slate-600">
        {translate('audio.credit')}{' '}
        <a href="http://creativecommons.org/licenses/by/3.0/" rel="noreferrer" target="_blank">
          CC BY 3.0
        </a>
      </p>
    </section>
  );
};

export default DiscoveryPanel;
